/**
 * /journals/new — Manual journal entry form.
 *
 * Multi-line table; each line has account autocomplete, memo, debit, credit.
 * Live balance check at the footer; submit disabled until:
 *   - ≥ 2 lines
 *   - every line has an account picked
 *   - every line has exactly one of debit/credit > 0
 *   - total debit == total credit (DB trigger requires this anyway)
 *
 * Two submit options:
 *   - "Save draft" → POST /api/journals (status=draft, can post later)
 *   - "Post" → creates draft, then POSTs /journals/{id}/post immediately
 *
 * Period is auto-derived from journal_date if exactly one fiscal period
 * contains it; otherwise the user picks one explicitly.
 *
 * Project / phase / cost_type are NOT exposed in the v1 form — manual JEs
 * only need them in <5% of cases. Use the full /api/journals POST if you
 * need cost-coded lines from a script.
 */
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Autocomplete, TextField } from "@mui/material";
import { MoneyInput } from "@/components/MoneyInput";

import { api } from "@/lib/api";
import {
  accountsListQueryOptions,
  glKeys,
  organizationCurrentQueryOptions,
  periodsListQueryOptions,
  trialBalanceKeys,
  type AccountSummary,
} from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import { DimensionSubRow } from "@/components/DimensionSubRow";
import type { FiscalPeriod } from "@/lib/types";
import { codeAndName, codeFor } from "../lib/displayCode";

export const Route = createFileRoute("/_auth/journals/new")({
  component: ManualJournalEntryPage,
});

interface DraftLine {
  /** Stable client-side key (not sent to backend). */
  uid: string;
  account: AccountSummary | null;
  debit: string;        // string so empty is distinguishable from 0
  credit: string;
  memo: string;
  currencyCode: string;
  fxRate: string;
  /** GL dimension selections: { [dimensionCode]: valueId } */
  dimensions: Record<string, string>;
}

const newLine = (): DraftLine => ({
  uid: crypto.randomUUID(),
  account: null,
  debit: "",
  credit: "",
  memo: "",
  currencyCode: "",
  fxRate: "1",
  dimensions: {},
});

function fmtMoney(n: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(n);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function ManualJournalEntryPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  // Header fields
  const [journalDate, setJournalDate] = useState(todayIso());
  const [periodId, setPeriodId] = useState<string>("");
  const [description, setDescription] = useState("");

  // Lines (start with 2 — min for a balanced JE)
  const [lines, setLines] = useState<DraftLine[]>(() => [newLine(), newLine()]);

  const accountsQuery = useQuery(accountsListQueryOptions());
  const periodsQuery = useQuery(periodsListQueryOptions());
  const organizationQuery = useQuery(organizationCurrentQueryOptions());
  const baseCurrency = organizationQuery.data?.base_currency_code ?? "";

  const postableAccounts = useMemo(
    () => (accountsQuery.data ?? []).filter((a) => a.is_postable),
    [accountsQuery.data],
  );

  // Auto-derive period from date when possible (single match wins)
  useEffect(() => {
    if (!periodsQuery.data || !journalDate) return;
    const matches = periodsQuery.data.filter(
      (p: FiscalPeriod) =>
        journalDate >= p.start_date && journalDate <= p.end_date,
    );
    if (matches.length === 1) {
      // Only auto-set if user hasn't explicitly chosen one yet, or if the
      // current selection no longer contains the date.
      const current = periodsQuery.data.find((p) => p.id === periodId);
      const stillValid =
        current && journalDate >= current.start_date && journalDate <= current.end_date;
      if (!stillValid) setPeriodId(matches[0].id);
    }
  }, [journalDate, periodsQuery.data, periodId]);

  // ── Validation ───────────────────────────────────────────────
  const totals = useMemo(() => {
    let debit = 0;
    let credit = 0;
    for (const ln of lines) {
      const d = Number(ln.debit) || 0;
      const c = Number(ln.credit) || 0;
      const fx = Number(ln.fxRate);
      if (!Number.isFinite(fx) || fx <= 0) continue;
      debit += Math.round(d * fx * 10_000) / 10_000;
      credit += Math.round(c * fx * 10_000) / 10_000;
    }
    const diff = debit - credit;
    return { debit, credit, diff, balanced: Math.abs(diff) < 0.00005 && debit > 0 };
  }, [lines]);

  type LineProblem = { uid: string; reason: string };
  const lineProblems = useMemo<LineProblem[]>(() => {
    const out: LineProblem[] = [];
    for (const ln of lines) {
      if (!ln.account) {
        out.push({ uid: ln.uid, reason: "no account picked" });
        continue;
      }
      if (ln.currencyCode.length !== 3) {
        out.push({ uid: ln.uid, reason: "currency is required" });
        continue;
      }
      const fx = Number(ln.fxRate);
      if (!Number.isFinite(fx) || fx <= 0) {
        out.push({ uid: ln.uid, reason: "FX rate must be greater than zero" });
        continue;
      }
      if (ln.currencyCode === baseCurrency && fx !== 1) {
        out.push({ uid: ln.uid, reason: `${baseCurrency} lines must use FX 1` });
        continue;
      }
      const d = Number(ln.debit) || 0;
      const c = Number(ln.credit) || 0;
      if (d === 0 && c === 0) {
        out.push({ uid: ln.uid, reason: "no amount" });
      } else if (d > 0 && c > 0) {
        out.push({ uid: ln.uid, reason: "both debit and credit set" });
      } else if (d < 0 || c < 0) {
        out.push({ uid: ln.uid, reason: "amounts must be ≥ 0" });
      }
    }
    return out;
  }, [baseCurrency, lines]);

  const valid =
    lines.length >= 2 &&
    lineProblems.length === 0 &&
    totals.balanced &&
    Boolean(periodId) &&
    Boolean(journalDate) &&
    Boolean(baseCurrency);

  // ── Mutations ────────────────────────────────────────────────
  const createJournal = useMutation({
    mutationFn: async (postImmediately: boolean) => {
      const body = {
        journal_date: journalDate,
        period_id: periodId,
        description: description || null,
        source: "manual",
        lines: lines.map((ln, i) => ({
          line_no: i + 1,
          account_id: ln.account!.id,
          debit: ln.debit || "0",
          credit: ln.credit || "0",
          currency_code: ln.currencyCode.toUpperCase(),
          fx_rate: ln.fxRate,
          memo: ln.memo || null,
          dimensions: Object.keys(ln.dimensions).length > 0 ? ln.dimensions : null,
        })),
      };
      const created = await api<{ id: string; journal_no: number }>(
        "/api/journals",
        { method: "POST", body },
      );
      if (postImmediately) {
        await api(`/api/journals/${created.id}/post`, { method: "POST" });
      }
      return created;
    },
    onSuccess: (created, postImmediately) => {
      qc.invalidateQueries({ queryKey: glKeys.all });
      qc.invalidateQueries({ queryKey: trialBalanceKeys.all });
      toast.success(
        postImmediately
          ? `Journal #${created.journal_no} posted`
          : `Journal #${created.journal_no} saved as draft`,
      );
      navigate({ to: "/gl" });
    },
    onError: (err: Error) => {
      toast.error(`Failed: ${err.message}`);
    },
  });

  // ── Helpers ──────────────────────────────────────────────────
  const updateLine = (uid: string, patch: Partial<DraftLine>) =>
    setLines((prev) => prev.map((l) => (l.uid === uid ? { ...l, ...patch } : l)));

  const removeLine = (uid: string) =>
    setLines((prev) => prev.length <= 2 ? prev : prev.filter((l) => l.uid !== uid));

  const addLine = () => setLines((prev) => [...prev, newLine()]);

  // Suggest the balancing amount on the last empty line
  const fillBalance = (uid: string) => {
    if (totals.balanced) return;
    const diff = totals.diff;
    const line = lines.find((candidate) => candidate.uid === uid);
    const fx = Number(line?.fxRate) || 1;
    const transactionAmount = Math.abs(diff) / fx;
    updateLine(
      uid,
      diff > 0
        ? { credit: String(transactionAmount), debit: "" }
        : { debit: String(transactionAmount), credit: "" },
    );
  };

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-auto">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="text-xs text-slate-500 mb-1 dark:text-slate-400">
          <Link to="/gl" className="hover:text-slate-700">General Ledger</Link>
          <span className="mx-2">/</span>
          <span className="font-medium text-slate-700 dark:text-slate-300">New journal</span>
        </div>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Manual journal entry</h1>
        <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">
          Add ≥ 2 lines that balance. Submit creates a draft; post writes to the GL
          (validates against DB triggers — balanced, in-period, postable accounts).
        </p>
      </div>

      <div className="flex-1 px-6 py-4 space-y-4">
        {/* Header card */}
        <section className="bg-white rounded-lg border border-slate-200 p-4 grid grid-cols-1 md:grid-cols-3 gap-3 dark:bg-gray-900 dark:border-gray-800">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5 block dark:text-slate-400">
              Journal date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={journalDate}
              onChange={(e) => setJournalDate(e.target.value)}
              className="border border-slate-300 rounded-md px-2 py-1 text-sm w-full dark:border-gray-700"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5 block dark:text-slate-400">
              Period <span className="text-red-500">*</span>
              <span className="ml-2 normal-case text-slate-400 dark:text-slate-500">
                (auto-set from date when one matches)
              </span>
            </label>
            <select
              value={periodId}
              onChange={(e) => setPeriodId(e.target.value)}
              className="border border-slate-300 rounded-md px-2 py-1 text-sm w-full bg-white dark:bg-gray-900 dark:border-gray-700"
            >
              <option value="">— pick a fiscal period —</option>
              {(periodsQuery.data ?? []).map((p) => (
                <option key={p.id} value={p.id} disabled={p.status !== "open"}>
                  Period {p.period_no} · {p.start_date} → {p.end_date} · {p.status}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5 block dark:text-slate-400">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Month-end accrual for utilities"
              className="border border-slate-300 rounded-md px-2 py-1 text-sm w-full dark:border-gray-700"
            />
          </div>
        </section>

        {/* Lines table */}
        <section className="bg-white rounded-lg border border-slate-200 p-4 dark:bg-gray-900 dark:border-gray-800">
          <h2 className="text-sm font-semibold text-slate-700 mb-3 dark:text-slate-300">
            Lines
            <span className="ml-2 text-xs font-normal text-slate-400 dark:text-slate-500">
              {lines.length} lines · base currency {baseCurrency || "loading…"} · balance must equal zero
            </span>
          </h2>

          <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-200 dark:border-gray-800 dark:text-slate-400">
                <th className="py-2 w-8">#</th>
                <th className="py-2 min-w-[280px]">Account</th>
                <th className="py-2 w-24">Currency</th>
                <th className="py-2 w-24 text-right">FX to {baseCurrency || "base"}</th>
                <th className="py-2">Memo</th>
                <th className="py-2 w-32 text-right">Debit</th>
                <th className="py-2 w-32 text-right">Credit</th>
                <th className="py-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((ln, i) => {
                const problem = lineProblems.find((p) => p.uid === ln.uid);
                return (
                  <Fragment key={ln.uid}>
                  <tr className="border-b border-slate-100 align-top dark:border-gray-800">
                    <td className="py-2 text-slate-400 font-mono dark:text-slate-500">{i + 1}</td>
                    <td className="py-2 pr-3">
                      <Autocomplete
                        size="small"
                        options={postableAccounts}
                        getOptionLabel={(a) => codeAndName(a)}
                        value={ln.account}
                        onChange={(_e, v) => updateLine(ln.uid, {
                          account: v,
                          currencyCode: v?.currency_code || baseCurrency,
                          fxRate: "1",
                        })}
                        loading={accountsQuery.isLoading}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        renderOption={(props, a) => {
                          const { key, ...rest } = props as { key: React.Key } & Record<string, unknown>;
                          return (
                            <li key={key} {...rest}>
                              <span style={{ fontFamily: "monospace", fontWeight: 600, marginRight: 8 }}>
                                {codeFor(a)}
                              </span>
                              <span>{a.name}</span>
                              <span style={{ marginLeft: "auto", fontSize: 10, color: "#94a3b8", textTransform: "uppercase" }}>
                                {a.account_type}
                              </span>
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Pick account by code or name"
                            error={problem?.reason === "no account picked"}
                          />
                        )}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="text"
                        value={ln.currencyCode}
                        readOnly
                        maxLength={3}
                        aria-label={`Line ${i + 1} currency`}
                        className="w-full border border-slate-300 rounded-md bg-slate-50 px-2 py-1 text-sm font-mono uppercase dark:border-gray-700 dark:bg-gray-800"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        min="0.00000001"
                        step="0.00000001"
                        value={ln.fxRate}
                        onChange={(e) => updateLine(ln.uid, { fxRate: e.target.value })}
                        disabled={ln.currencyCode === baseCurrency}
                        aria-label={`Line ${i + 1} FX rate to ${baseCurrency || "base currency"}`}
                        className="w-full border border-slate-300 rounded-md px-2 py-1 text-sm font-mono text-right disabled:bg-slate-100 dark:border-gray-700 dark:disabled:bg-gray-800"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="text"
                        value={ln.memo}
                        onChange={(e) => updateLine(ln.uid, { memo: e.target.value })}
                        placeholder="Per-line memo (optional)"
                        className="w-full border border-slate-300 rounded-md px-2 py-1 text-sm dark:border-gray-700"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <MoneyInput
                        value={ln.debit}
                        onChange={(v) => updateLine(ln.uid, { debit: v, credit: v ? "" : ln.credit })}
                        onBlur={() => i === lines.length - 1 && fillBalance(ln.uid)}
                        placeholder="0.00"
                        className="border-slate-300 dark:border-gray-700"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <MoneyInput
                        value={ln.credit}
                        onChange={(v) => updateLine(ln.uid, { credit: v, debit: v ? "" : ln.debit })}
                        onBlur={() => i === lines.length - 1 && fillBalance(ln.uid)}
                        placeholder="0.00"
                        className="border-slate-300 dark:border-gray-700"
                      />
                    </td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        onClick={() => removeLine(ln.uid)}
                        disabled={lines.length <= 2}
                        title={lines.length <= 2 ? "minimum 2 lines" : "remove line"}
                        className="text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed dark:text-slate-500"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                  <DimensionSubRow
                    colSpan={8}
                    dimValues={ln.dimensions}
                    onChange={(d) => updateLine(ln.uid, { dimensions: d })}
                  />
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          </div>

          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              onClick={addLine}
              className="text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md px-3 py-1 dark:bg-gray-800 dark:text-slate-300"
            >
              + Add line
            </button>
            {lineProblems.length > 0 && (
              <span className="text-xs text-amber-700">
                {lineProblems.length} line{lineProblems.length === 1 ? "" : "s"} need attention
              </span>
            )}
          </div>
        </section>
      </div>

      {/* Sticky footer with totals + actions */}
      <div
        className={`border-t px-6 py-3 flex items-center gap-6 text-sm ${
          totals.balanced
            ? "bg-green-50 border-green-200"
            : "bg-amber-50 border-amber-300"
        }`}
      >
        <div>
          <span className="text-xs uppercase tracking-wide text-slate-500 mr-2 dark:text-slate-400">
            Total Debit
          </span>
          <span className="font-mono font-semibold">{fmtMoney(totals.debit, baseCurrency || "USD")}</span>
        </div>
        <div>
          <span className="text-xs uppercase tracking-wide text-slate-500 mr-2 dark:text-slate-400">
            Total Credit
          </span>
          <span className="font-mono font-semibold">{fmtMoney(totals.credit, baseCurrency || "USD")}</span>
        </div>
        {!totals.balanced && (
          <div>
            <span className="text-xs uppercase tracking-wide text-amber-700 mr-2">
              Difference
            </span>
            <span className="font-mono font-bold text-amber-800">
              {fmtMoney(totals.diff, baseCurrency || "USD")}
            </span>
          </div>
        )}
        <span
          className={`px-2 py-0.5 text-xs uppercase tracking-wide font-semibold rounded ${
            totals.balanced
              ? "bg-green-100 text-green-800"
              : "bg-amber-100 text-amber-800"
          }`}
        >
          {totals.balanced ? "✓ Balanced" : "⚠ Out of balance"}
        </span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => navigate({ to: "/gl" })}
          className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5 dark:text-slate-300"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => createJournal.mutate(false)}
          disabled={!valid || createJournal.isPending}
          className="text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-slate-300"
        >
          {createJournal.isPending ? "Saving…" : "Save draft"}
        </button>
        <button
          type="button"
          onClick={() => createJournal.mutate(true)}
          disabled={!valid || createJournal.isPending}
          className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Validates against DB triggers (balanced, period open, postable accounts), writes to journal_lines"
        >
          {createJournal.isPending ? "Posting…" : "Post to GL"}
        </button>
      </div>
    </div>
  );
}
